"use client";

import React from "react";
import FilterSection from "./FilterSection";
import AdminScopePills from "./AdminScopePills";
import { ContestSortSection, SortBy, SortOrder } from "./ContestSortSection";
import { UserMultiSelectBottomSheet } from "../category/UserMultiSelectBottomSheet";
import { MultiSelectBottomSheet } from "../category/MultiSelectBottomSheet";
import { PeriodRangePicker } from "../filters/PeriodRangePicker";
import type { AdminScope } from "@/components/contest/list/AdminScopePills";
import { i18nKey } from "@/lib/react/i18n/types/locale";
import { LockFilter } from "@/restapi/contest";

type UserSheetItem = {
  id: string;
  name: string;
};

type CategoryItem = {
  id: number;
  name: string;
};

type ContestFilterSectionProps = {
  open: boolean;
  onToggle: () => void;
  title: string;
  subtitle: string;

  canShowScopeUI: boolean;
  adminScope: AdminScope;
  onChangeAdminScope: (v: AdminScope) => void;

  t: (key: i18nKey) => string;

  sortBy: SortBy;
  sortOrder: SortOrder;
  onChangeSortBy: (v: SortBy) => void;
  onChangeSortOrder: (v: SortOrder) => void;
  onCommitSearch: () => void;

  canUseUserSelect: boolean;
  userLabelKey: i18nKey;
  selectedUserIds: string[];
  onChangeSelectedUserIds: (ids: string[]) => void;
  userSheetItems: UserSheetItem[];
  userSearchValue: string;
  handleUserSearchCommit: (keyword: string) => void;
  userLoadMore: () => void;
  userHasMore: boolean;
  usersLoadingInitial: boolean;

  selectedCategoryIds: number[];
  onChangeSelectedCategoryIds: (ids: number[]) => void;
  categories: CategoryItem[];
  categorySearchValue: string;
  handleCategorySearchCommit: (keyword: string) => void;
  categoryLoadMore: () => void;
  categoryHasMore: boolean;

  startFrom: string | null;
  startTo: string | null;
  onChangeStartFrom: (v: string | null) => void;
  onChangeStartTo: (v: string | null) => void;
  locale: string;

  hasAnyFilter: boolean;
  clearFilters: () => void;
  lockFilter: LockFilter;
  onChangeLockFilter: (v: LockFilter) => void;
};

const ContestFilterSection = ({
  open,
  onToggle,
  title,
  subtitle,
  canShowScopeUI,
  adminScope,
  onChangeAdminScope,
  t,
  sortBy,
  sortOrder,
  onChangeSortBy,
  onChangeSortOrder,
  onCommitSearch,
  canUseUserSelect,
  userLabelKey,
  selectedUserIds,
  onChangeSelectedUserIds,
  userSheetItems,
  userSearchValue,
  handleUserSearchCommit,
  userLoadMore,
  userHasMore,
  usersLoadingInitial,
  selectedCategoryIds,
  onChangeSelectedCategoryIds,
  categories,
  categorySearchValue,
  handleCategorySearchCommit,
  categoryLoadMore,
  categoryHasMore,
  startFrom,
  startTo,
  onChangeStartFrom,
  onChangeStartTo,
  locale,
  hasAnyFilter,
  clearFilters,
  onChangeLockFilter,
  lockFilter,
}: ContestFilterSectionProps) => {
  return (
    <FilterSection open={open} onToggle={onToggle} title={title} subtitle={subtitle}>
      {canShowScopeUI && (
        <div className="pt-2">
          <p className="text-xs px-2 text-white/70">{t("Scope")}</p>
          <div>
            <AdminScopePills
              value={adminScope}
              onChange={(next) => {
                onChangeAdminScope(next);
                if (next === "MINE") onChangeSelectedUserIds([]);
                onCommitSearch();
              }}
            />
          </div>
          <div className="mb-2 px-2">
            <p className="gap-2 pb-2 text-xs text-white/80">{t("Lock status")}</p>
            <div className="flex flex-wrap gap-2">
              {[
                { key: "all", label: t("All") },
                { key: "locked", label: t("Locked") },
                { key: "unlocked", label: t("Unlocked") },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  data-testid={`lock-filter-${item.key}`}
                  onClick={() => onChangeLockFilter(item.key as "all" | "locked" | "unlocked")}
                  className={[
                    "rounded-full border px-4 py-2 text-sm transition",
                    lockFilter === item.key
                      ? "bg-white/15 border-white/40 text-white"
                      : "bg-transparent border-white/15 text-white/70 hover:bg-white/5",
                  ].join(" ")}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <ContestSortSection
        t={t}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onChange={({ sortBy, sortOrder }) => {
          onChangeSortBy(sortBy);
          onChangeSortOrder(sortOrder);
        }}
        onCommit={onCommitSearch}
      />


      {canUseUserSelect && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs px-2 text-white/70">{t(userLabelKey)}</p>
            {selectedUserIds.length > 0 && (
              <button
                type="button"
                onClick={() => onChangeSelectedUserIds([])}
                className="text-xs text-white/60 hover:text-white"
              >
                {t("Clear")}
              </button>
            )}
          </div>

          <UserMultiSelectBottomSheet
            title={t(userLabelKey)}
            placeholder={t("Search")}
            items={userSheetItems}
            value={selectedUserIds}
            onChange={onChangeSelectedUserIds}
            buttonLabelEmpty={t("Select Contest organizer")}
            buttonLabelFilled={t("Edit")}
            showChips={true}
            isFormMode={true}
            searchValue={userSearchValue}
            onSearchCommit={handleUserSearchCommit}
            loadMore={userLoadMore}
            hasMore={userHasMore}
            scrollAreaId="userScrollArea"
            mode="multi"
            closeOnSelect={false}
          />
          {usersLoadingInitial && <div className="mt-2 text-xs text-white/40 px-2">Loading...</div>}
        </div>
      )}

      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs px-2 text-white/70">{t("Category")}</p>
          {selectedCategoryIds.length > 0 && (
            <button
              type="button"
              onClick={() => onChangeSelectedCategoryIds([])}
              className="text-xs text-white/60 hover:text-white"
            >
              {t("Clear")}
            </button>
          )}
        </div>

        <MultiSelectBottomSheet
          loadMore={categoryLoadMore}
          hasMore={categoryHasMore}
          title={t("Category")}
          placeholder={t("Search category")}
          items={categories}
          value={selectedCategoryIds}
          onChange={onChangeSelectedCategoryIds}
          buttonLabelEmpty={t("Select category")}
          buttonLabelFilled={t("Edit category selection")}
          showChips={false}
          isFormMode={true}
          searchValue={categorySearchValue}
          onSearchCommit={handleCategorySearchCommit}
          scrollAreaId="categoryScrollArea"
          triggerTestId="category-select-button"
          sheetTestId="category-select-sheet"
        />
      </div>

      <div className="mt-3">
        <PeriodRangePicker
          startFrom={startFrom}
          startTo={startTo}
          onChangeStartFrom={onChangeStartFrom}
          onChangeStartTo={onChangeStartTo}
          locale={locale === "ja" ? "ja" : "en"}
          label={t("Period")}
          startPlaceholder={t("Start date")}
          endPlaceholder={t("End date")}
          emptyHint={t("No period specified")}
          invalidMessage={t("End date must be on or after start date")}
          testIdStart="period-filter-from"
          testIdEnd="period-filter-to"
          portalId="datepicker-portal"
          endMustBeAfterStart={false}
        />
      </div>

      {hasAnyFilter && (
        <div className="pt-4">
          <button
            type="button"
            onClick={clearFilters}
            className="w-full rounded-xl px-4 py-3 border border-white/15 bg-white/5 text-sm text-white/80 hover:bg-white/10"
          >
            {t("Clear all filters")}
          </button>
        </div>
      )}
    </FilterSection>
  );
};

export default ContestFilterSection;